// DashboardFinancieroTab.js — dashboard financiero de dirección de
// Climatización Zapata (Facturas → Analítica). Responde cada mes a las cinco
// preguntas acordadas: rentabilidad y margen, dinero generado sin cobrar,
// caja para 3 meses, crecimiento del ingreso recurrente, y si vamos por
// delante o detrás del plan.
//
// Fase 1 (aprobada): secciones 1/2/3/6/8/9 completas + sección 4 parcial
// (garantías Ariston, órdenes sin facturar, facturas pendientes con DSO).
// Fase 2 (pendiente, cuando haya datos fiables): previsión de caja a 90 días,
// productividad por técnico, coste de material real (Almacén), devoluciones
// de facturas normales desde movimientos_banco.
//
// Todo lo que se ve aquí viene de resumenFinancieroMensual, pre-agregado por
// recalcularResumenFinanciero.js (cron nocturno) — nunca se recalcula en vivo
// en esta pantalla, salvo la sección 4 (dinero pendiente HOY, no tiene
// sentido como snapshot de un mes ya cerrado).
import React, { useState, useEffect, useCallback } from 'react';
import { authService } from '../../services/authService';
import PlanAnualPanel from './PlanAnualPanel';
import ValidacionClasificacionPanel from './ValidacionClasificacionPanel';
import ReglasGastosPanel, { CATEGORIAS_GASTO } from './ReglasGastosPanel';
import GastosEfectivoPanel from './GastosEfectivoPanel';
import ValidacionGastosPanel from './ValidacionGastosPanel';

// Garantías: no van aquí — Ariston nos autofactura directamente y esas
// facturas nunca están en nuestra colección `facturas` (pendiente decidir la
// fuente: albaranes/liquidaciones de Ariston, o sus cobros en movimientos_banco).
const LINEA_LABEL = {
  mantenimiento: 'Mantenimiento', sat: 'SAT fuera de garantía',
  instalacion: 'Instalaciones', repuestos: 'Repuestos', abono: 'Abonos',
};
const LINEAS_ORDEN = ['mantenimiento', 'sat', 'instalacion', 'repuestos', 'abono'];

// Categorías operativas (restan antes de "Resultado antes de dirección") —
// el resto de _categoriasGastos.js (Dirección/IVA/IRPF/Inversión/Traspaso)
// tiene su propio tratamiento en la cuenta de resultados.
const CATEGORIAS_OPERATIVAS = CATEGORIAS_GASTO.filter(c => !['direccion', 'iva', 'irpf_titular', 'inversion', 'traspaso_interno'].includes(c.key));

function fmtEur(n) {
  if (n == null) return '—';
  return Number(n).toLocaleString('es-ES', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' €';
}
function mesLabel(mesStr) {
  if (!mesStr) return '—';
  const [y, m] = mesStr.split('-');
  return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString('es-ES', { month: 'short', year: '2-digit' });
}

// Agrupa los 12 meses de rentabilidad.cuentaResultadosMensual en 4
// trimestres — pedido explícitamente ("vista trimestral además de la
// mensual"). Se calcula aquí, sobre los datos mensuales ya traídos, sin
// pedir nada nuevo al backend.
function construirTrimestral(mensual) {
  return Array.from({ length: 4 }, (_, q) => {
    const meses = mensual.slice(q * 3, q * 3 + 3);
    const calculados = meses.filter(m => m.calculado);
    const anioMuestra = meses.find(m => m.mes)?.mes?.slice(0, 4);
    const etiqueta = `T${q + 1}${anioMuestra ? ` ${anioMuestra}` : ''}`;
    if (calculados.length === 0) return { mes: etiqueta, calculado: false };

    const porCategoria = {};
    for (const c of CATEGORIAS_GASTO) porCategoria[c.key] = calculados.reduce((s, m) => s + (m.porCategoria?.[c.key] || 0), 0);
    const ingresosNeta = calculados.reduce((s, m) => s + m.ingresosNeta, 0);
    const antesDeDireccion = calculados.reduce((s, m) => s + (m.antesDeDireccion || 0), 0);
    const antesImpuestos = calculados.reduce((s, m) => s + (m.antesImpuestos || 0), 0);
    const neto = calculados.reduce((s, m) => s + (m.neto || 0), 0);
    const completo = calculados.length === 3 && calculados.every(m => m.completo);
    const materialPct = ingresosNeta ? Math.round((porCategoria.material / ingresosNeta) * 1000) / 10 : 0;
    const sinClasificar = {
      count: calculados.reduce((s, m) => s + (m.sinClasificar?.count || 0), 0),
      total: calculados.reduce((s, m) => s + (m.sinClasificar?.total || 0), 0),
    };

    return {
      mes: etiqueta, calculado: true, completo,
      ingresosNeta, porCategoria, antesDeDireccion, antesImpuestos, neto,
      margenPct: completo && ingresosNeta ? Math.round((antesImpuestos / ingresosNeta) * 1000) / 10 : null,
      materialPct, alertaMaterial: false, sinClasificar,
    };
  });
}

function SemaforoCard({ label, value, sub, estado }) {
  const colores = {
    verde: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    ambar: 'bg-amber-50 border-amber-200 text-amber-800',
    rojo:  'bg-red-50 border-red-200 text-red-800',
    neutro: 'bg-gray-50 border-gray-200 text-gray-700',
  };
  return (
    <div className={`rounded-2xl border shadow-sm px-4 py-4 ${colores[estado] || colores.neutro}`}>
      <p className="text-xl font-bold">{value}</p>
      <p className="text-xs font-bold mt-1">{label}</p>
      {sub && <p className="text-[11px] opacity-70 mt-0.5">{sub}</p>}
    </div>
  );
}

// Editor inline del importe por defecto (config/garantiasAriston) — solo
// admin. Se usa cuando no hay suficiente histórico real de liquidaciones de
// los últimos 6 meses (ver mediaRealUltimos6Meses en getDashboardFinanciero.js).
function EditorImporteGarantia({ valorActual, onGuardado }) {
  const [editando, setEditando] = useState(false);
  const [valor, setValor] = useState(valorActual);
  const [guardando, setGuardando] = useState(false);

  const guardar = async () => {
    setGuardando(true);
    try {
      const res = await fetch('/.netlify/functions/saveConfigGarantias', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({ importeEstimadoPorOrden: Number(valor) }),
      });
      const data = await res.json();
      if (data.success) { setEditando(false); onGuardado(); }
    } finally {
      setGuardando(false);
    }
  };

  if (!editando) {
    return <button onClick={() => setEditando(true)} className="text-[11px] text-blue-600 hover:underline mt-1">Cambiar importe por defecto</button>;
  }
  return (
    <div className="flex items-center gap-1.5 mt-1">
      <input type="number" className="border rounded px-1.5 py-1 text-xs w-20" value={valor} onChange={e => setValor(e.target.value)} />
      <button onClick={guardar} disabled={guardando} className="text-[11px] font-bold text-emerald-600 hover:underline disabled:opacity-60">Guardar</button>
      <button onClick={() => setEditando(false)} className="text-[11px] text-gray-400 hover:underline">Cancelar</button>
    </div>
  );
}

export default function DashboardFinancieroTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [subTab, setSubTab] = useState('resumen'); // 'resumen' | 'plan' | 'reglas' | 'efectivo'
  const [periodo, setPeriodo] = useState('mensual'); // 'mensual' | 'trimestral'
  const [recalculando, setRecalculando] = useState(false);
  const [recalcMsg, setRecalcMsg] = useState('');
  const [backfillEnCurso, setBackfillEnCurso] = useState(false);
  const [backfillMsg, setBackfillMsg] = useState('');

  const esAdmin = authService.getCurrentUser()?.role === 'admin';

  const cargar = useCallback(() => {
    setLoading(true); setError('');
    fetch('/.netlify/functions/getDashboardFinanciero', { headers: { ...authService.authHeader() } })
      .then(r => r.json())
      .then(d => { if (!d.success) { setError(d.error || 'Error desconocido'); return; } setData(d); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const recalcularAhora = async () => {
    setRecalculando(true); setRecalcMsg('');
    try {
      const res = await fetch('/.netlify/functions/forzarRecalcularResumenFinanciero', {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authService.authHeader() }, body: JSON.stringify({}),
      });
      const d = await res.json();
      if (!d.success) throw new Error(d.error);
      setRecalcMsg('✓ Recalculado');
      cargar();
    } catch (e) {
      setRecalcMsg(`Error: ${e.message}`);
    } finally {
      setRecalculando(false);
      setTimeout(() => setRecalcMsg(''), 5000);
    }
  };

  // Recalcula un rango de meses uno a uno (mismo patrón resumible que
  // "Importar histórico completo" en MigracionView.js) — necesario para
  // poder revisar la clasificación por línea de años enteros (2025/2026),
  // ya que el cron solo mantiene al día el mes en curso y el anterior.
  const recalcularHistorico = async () => {
    if (!window.confirm('Recalcula mes a mes desde enero de 2025 hasta el mes en curso. Puede tardar varios minutos — no cierres esta pestaña.')) return;
    setBackfillEnCurso(true); setBackfillMsg('');
    const hoy = new Date();
    const meses = [];
    for (let y = 2025; y <= hoy.getFullYear(); y++) {
      const ultimoMes = y === hoy.getFullYear() ? hoy.getMonth() + 1 : 12;
      for (let m = 1; m <= ultimoMes; m++) meses.push(`${y}-${String(m).padStart(2, '0')}`);
    }
    try {
      for (let i = 0; i < meses.length; i++) {
        setBackfillMsg(`Recalculando ${meses[i]}… (${i + 1}/${meses.length})`);
        const res = await fetch('/.netlify/functions/forzarRecalcularResumenFinanciero', {
          method: 'POST', headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
          body: JSON.stringify({ mes: meses[i] }),
        });
        const d = await res.json();
        if (!d.success) throw new Error(`${meses[i]}: ${d.error}`);
      }
      setBackfillMsg(`✓ Histórico recalculado (${meses.length} meses)`);
      cargar();
    } catch (e) {
      setBackfillMsg(`Error: ${e.message}`);
    } finally {
      setBackfillEnCurso(false);
    }
  };

  if (loading) return <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-500" /></div>;
  if (error) return (
    <div className="space-y-3">
      <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-2 text-sm">{error}</div>
      <button onClick={cargar} className="text-xs font-bold text-red-600 hover:text-red-800">Reintentar</button>
    </div>
  );
  if (!data) return null;

  const { semaforo, ingresos, rentabilidad, pendiente, recurrente, calidadDato, anio, anioAnterior, mesActual } = data;

  // null = incompleto (faltan costes fijos o compras de material de algún
  // mes) — nunca se enseña un % calculado con datos a medias.
  const margenIncompleto = semaforo.margenAnoPct == null;
  const margenEstado = margenIncompleto ? 'neutro' : semaforo.margenAnoPct >= 25 ? 'verde' : semaforo.margenAnoPct >= 20 ? 'ambar' : 'rojo';
  const facturacionAnoPct = data.ingresos.facturacionNetaAnoAnterior
    ? Math.round((data.ingresos.facturacionNetaAno / data.ingresos.facturacionNetaAnoAnterior) * 1000) / 10
    : null;

  return (
    <div className="space-y-4 pb-12">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex gap-1">
          {[['resumen', 'Resumen'], ['plan', 'Plan anual'], ['reglas', 'Reglas de gasto'], ['efectivo', 'Gastos en efectivo']].map(([key, label]) => (
            <button
              key={key} onClick={() => setSubTab(key)}
              className={`px-3 py-2 text-xs font-bold rounded-xl transition-colors ${subTab === key ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            >{label}</button>
          ))}
        </div>
        {subTab === 'resumen' && esAdmin && (
          <div className="flex items-center gap-2">
            {recalcMsg && <span className="text-xs font-semibold text-gray-500">{recalcMsg}</span>}
            {backfillMsg && <span className="text-xs font-semibold text-gray-500">{backfillMsg}</span>}
            <button onClick={recalcularHistorico} disabled={backfillEnCurso} className="px-3 py-2 bg-gray-100 text-gray-700 text-xs font-bold rounded-xl hover:bg-gray-200 disabled:opacity-60">
              {backfillEnCurso ? 'Recalculando…' : 'Recalcular histórico'}
            </button>
            <button onClick={recalcularAhora} disabled={recalculando} className="px-3 py-2 bg-red-600 text-white text-xs font-bold rounded-xl hover:bg-red-700 disabled:opacity-60">
              {recalculando ? 'Recalculando…' : 'Recalcular ahora'}
            </button>
          </div>
        )}
      </div>

      {subTab === 'reglas' && <ReglasGastosPanel />}
      {subTab === 'efectivo' && <GastosEfectivoPanel />}
      {subTab === 'plan' && <PlanAnualPanel />}

      {subTab === 'resumen' && (
        <>
          <p className="text-[11px] text-gray-400">Datos a fecha de {mesActual} (calculados de madrugada, no en vivo — usa "Recalcular ahora" si acabas de cambiar algo).</p>

          {/* Sección 1 — Semáforo de salud */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <SemaforoCard
              label={`Margen antes de impuestos (${anio})`}
              value={margenIncompleto ? 'Incompleto' : `${semaforo.margenAnoPct}%`}
              estado={margenEstado}
              sub={margenIncompleto ? 'Faltan costes fijos o compras de material de algún mes' : 'Objetivo ≥25% · mínimo 20%'}
            />
            <SemaforoCard
              label="Caja actual"
              value={semaforo.cajaActual != null ? fmtEur(semaforo.cajaActual) : '—'}
              estado={semaforo.cajaActual == null ? 'neutro' : 'verde'}
              sub="vs. 3 meses de costes fijos (Fase 2: umbral automático)"
            />
            <SemaforoCard label="% ingreso recurrente" value={`${semaforo.pctRecurrenteAno}%`} estado="neutro" sub={`Objetivo del plan: ver pestaña Plan anual`} />
            <SemaforoCard
              label={`Facturación ${anio} vs ${anioAnterior}`}
              value={facturacionAnoPct != null ? `${facturacionAnoPct}%` : '—'}
              estado="neutro"
              sub={fmtEur(ingresos.facturacionNetaAno)}
            />
            <SemaforoCard
              label="Dinero pendiente total"
              value={fmtEur(semaforo.dineroPendienteTotal)}
              estado={semaforo.dineroPendienteTotal > (ingresos.facturacionNetaAno / 12) ? 'rojo' : 'verde'}
              sub="Garantías + sin facturar + pendiente de cobro"
            />
          </div>

          {/* Sección 2 — Ingresos */}
          <div className="bg-white rounded-2xl border shadow-sm p-5">
            <h3 className="font-bold text-gray-900 mb-3 text-sm">Ingresos por línea de negocio ({anio})</h3>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {LINEAS_ORDEN.filter(l => l !== 'abono').map(l => {
                const v = ingresos.porLineaAno[l];
                const pct = ingresos.facturacionNetaAno ? Math.round((v.neta / ingresos.facturacionNetaAno) * 1000) / 10 : 0;
                return (
                  <div key={l} className={`rounded-xl border px-4 py-3 ${l === 'sin_clasificar' && v.count > 0 ? 'border-red-200 bg-red-50' : 'border-gray-100 bg-gray-50'}`}>
                    <p className="text-xs font-bold text-gray-500 uppercase">{LINEA_LABEL[l]}</p>
                    <p className="text-lg font-bold text-gray-900 mt-1">{fmtEur(v.neta)}</p>
                    <p className="text-[11px] text-gray-400">{pct}% · {v.count} facturas · ticket medio {fmtEur(ingresos.ticketMedioPorLinea[l])}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Sección 3 — Rentabilidad */}
          <div className="bg-white rounded-2xl border shadow-sm overflow-hidden">
            <div className="px-5 py-3 border-b bg-gray-50 flex items-center justify-between flex-wrap gap-2">
              <div>
                <h3 className="font-bold text-sm text-gray-900">Cuenta de resultados ({anio})</h3>
                <p className="text-[11px] text-amber-700 font-semibold mt-0.5">Costes según pagos del banco (criterio de caja)</p>
                <p className="text-[11px] text-gray-400 mt-0.5">{rentabilidad.margenContribucionNota}</p>
              </div>
              <div className="flex gap-1">
                {[['mensual', 'Mensual'], ['trimestral', 'Trimestral']].map(([key, label]) => (
                  <button
                    key={key} onClick={() => setPeriodo(key)}
                    className={`px-2.5 py-1.5 text-[11px] font-bold rounded-lg transition-colors ${periodo === key ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                  >{label}</button>
                ))}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b bg-gray-50/60">
                    <th className="text-left px-4 py-2 font-bold text-gray-500 uppercase">{periodo === 'mensual' ? 'Mes' : 'Trimestre'}</th>
                    <th className="text-right px-3 py-2 font-bold text-emerald-600 uppercase">Ingresos</th>
                    {CATEGORIAS_OPERATIVAS.map(c => (
                      <th key={c.key} className="text-right px-3 py-2 font-bold text-gray-500 uppercase whitespace-nowrap">{c.label}</th>
                    ))}
                    <th className="text-right px-4 py-2 font-bold text-gray-900 uppercase whitespace-nowrap">Antes de dirección</th>
                    <th className="text-right px-3 py-2 font-bold text-gray-500 uppercase">Dirección</th>
                    <th className="text-right px-4 py-2 font-bold text-gray-900 uppercase whitespace-nowrap">Antes de impuestos</th>
                    <th className="text-right px-3 py-2 font-bold text-gray-500 uppercase whitespace-nowrap">IRPF titular</th>
                    <th className="text-right px-4 py-2 font-bold text-gray-900 uppercase">Resultado neto</th>
                    <th className="text-right px-4 py-2 font-bold text-gray-900 uppercase">Margen%</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {(periodo === 'mensual' ? rentabilidad.cuentaResultadosMensual : construirTrimestral(rentabilidad.cuentaResultadosMensual)).map(m => (
                    <tr key={m.mes} className={!m.calculado ? 'opacity-40' : ''}>
                      <td className="px-4 py-2 font-semibold text-gray-700 whitespace-nowrap">{periodo === 'mensual' ? mesLabel(m.mes) : m.mes}</td>
                      {m.calculado ? (
                        <>
                          <td className="px-3 py-2 text-right text-emerald-600 font-semibold">{fmtEur(m.ingresosNeta)}</td>
                          {CATEGORIAS_OPERATIVAS.map(c => (
                            <td key={c.key} className={`px-3 py-2 text-right ${c.key === 'material' && m.alertaMaterial ? 'text-red-600 font-bold' : 'text-gray-500'}`}>
                              {fmtEur(m.porCategoria?.[c.key])}
                              {c.key === 'material' && <span className="text-[10px]"> ({m.materialPct}%){m.alertaMaterial ? ' ⚠' : ''}</span>}
                            </td>
                          ))}
                          <td className="px-4 py-2 text-right font-bold text-gray-900">{fmtEur(m.antesDeDireccion)}</td>
                          <td className="px-3 py-2 text-right text-gray-500">{fmtEur(m.porCategoria?.direccion)}</td>
                          <td className="px-4 py-2 text-right font-bold text-gray-900">{fmtEur(m.antesImpuestos)}</td>
                          <td className="px-3 py-2 text-right text-gray-500">{fmtEur(m.porCategoria?.irpf_titular)}</td>
                          <td className={`px-4 py-2 text-right font-bold ${m.neto >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{fmtEur(m.neto)}</td>
                          <td className="px-4 py-2 text-right font-bold text-gray-700">{m.margenPct == null ? <span className="text-gray-300 font-normal">Incompleto</span> : `${m.margenPct}%`}</td>
                        </>
                      ) : (
                        <td colSpan={CATEGORIAS_OPERATIVAS.length + 6} className="px-3 py-2 text-center text-gray-300">sin calcular</td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="px-5 py-2 text-[11px] text-gray-400 border-t">IVA, Inversión y Traspaso interno no entran en este resultado — son movimiento de caja real pero no gasto ni ingreso de la actividad.</p>
          </div>

          <ValidacionGastosPanel />

          {/* Sección 4 (parcial) — Dinero pendiente */}
          <div className="bg-white rounded-2xl border shadow-sm p-5">
            <h3 className="font-bold text-gray-900 mb-1 text-sm">Dinero pendiente (generado y sin cobrar)</h3>
            <p className="text-[11px] text-gray-400 mb-3">Garantías: solo Ariston (única marca con flujo de liquidación completo). Fecha límite de liquidación estimada a partir de la fecha de la visita + 12 meses.</p>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
              <div className={`rounded-xl border px-4 py-3 ${pendiente.garantiasAriston.caducanPronto > 0 ? 'border-red-200 bg-red-50' : 'border-gray-100 bg-gray-50'}`}>
                <p className="text-xs font-bold text-gray-500 uppercase">Garantías Ariston pendientes</p>
                <p className="text-lg font-bold text-gray-900 mt-1">{fmtEur(pendiente.garantiasAriston.importe)}</p>
                <p className="text-[11px] text-gray-400">{pendiente.garantiasAriston.count} órdenes</p>
                <p className="text-[11px] mt-1">
                  0-3m: {pendiente.garantiasAriston.antiguedad.de0a3} · 3-6m: {pendiente.garantiasAriston.antiguedad.de3a6} · 6-9m: {pendiente.garantiasAriston.antiguedad.de6a9} · 9-12m: {pendiente.garantiasAriston.antiguedad.de9a12}
                </p>
                {pendiente.garantiasAriston.esEstimado && (
                  <p className="text-[11px] text-amber-600 mt-1">
                    Estimado a {fmtEur(pendiente.garantiasAriston.importePorOrden)}/orden ({pendiente.garantiasAriston.metodoEstimacion === 'media_real_6_meses' ? 'media real últimos 6 meses' : 'valor por defecto'})
                  </p>
                )}
                {pendiente.garantiasAriston.caducanPronto > 0 && (
                  <p className="text-[11px] text-red-600 font-bold mt-1">⚠ {pendiente.garantiasAriston.caducanPronto} caducan en menos de 60 días</p>
                )}
                {pendiente.garantiasAriston.error && <p className="text-[11px] text-red-600 font-bold mt-1">⚠ No se pudo calcular: {pendiente.garantiasAriston.error}</p>}
                {esAdmin && <EditorImporteGarantia valorActual={pendiente.garantiasAriston.importePorOrden} onGuardado={cargar} />}
              </div>
              <div className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3">
                <p className="text-xs font-bold text-gray-500 uppercase">Órdenes completadas sin facturar</p>
                <p className="text-lg font-bold text-gray-900 mt-1">{fmtEur(pendiente.ordenesSinFacturar.importe)}</p>
                <p className="text-[11px] text-gray-400">{pendiente.ordenesSinFacturar.count} órdenes</p>
                {pendiente.ordenesSinFacturar.error && <p className="text-[11px] text-red-600 font-bold mt-1">⚠ No se pudo calcular: {pendiente.ordenesSinFacturar.error}</p>}
              </div>
              <div className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3">
                <p className="text-xs font-bold text-gray-500 uppercase">Facturas pendientes de cobro</p>
                <p className="text-lg font-bold text-gray-900 mt-1">{fmtEur(pendiente.facturasPendientes.importe)}</p>
                <p className="text-[11px] text-gray-400">{pendiente.facturasPendientes.count} facturas · DSO {pendiente.facturasPendientes.dso} días</p>
                {pendiente.facturasPendientes.error && <p className="text-[11px] text-red-600 font-bold mt-1">⚠ No se pudo calcular: {pendiente.facturasPendientes.error}</p>}
                {pendiente.facturasPendientes.ejemplos?.length > 0 && (
                  <details className="mt-2">
                    <summary className="text-[11px] text-blue-600 cursor-pointer">Ver ejemplos ({pendiente.facturasPendientes.ejemplos.length})</summary>
                    <ul className="mt-1 space-y-1">
                      {pendiente.facturasPendientes.ejemplos.map((e, i) => (
                        <li key={i} className="text-[10px] text-gray-500 font-mono">
                          {e.referencia} · {e.fecha} · {fmtEur(e.importeTotal)} · pendiente StelOrder: {e.remainingAmount != null ? fmtEur(e.remainingAmount) : '—'} · cobro registrado: {e.tieneCobro ? 'sí' : 'no'}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            </div>
          </div>

          {/* Sección 6 — Ingreso recurrente */}
          <div className="bg-white rounded-2xl border shadow-sm p-5">
            <h3 className="font-bold text-gray-900 mb-3 text-sm">Ingreso recurrente (mantenimiento)</h3>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
              <div className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3">
                <p className="text-xs font-bold text-gray-500 uppercase">ARR</p>
                <p className="text-lg font-bold text-gray-900 mt-1">{fmtEur(recurrente.arr)}</p>
                <p className="text-[11px] text-gray-400">MRR {fmtEur(recurrente.mrr)}</p>
              </div>
              <div className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3">
                <p className="text-xs font-bold text-gray-500 uppercase">Contratos vigentes</p>
                <p className="text-lg font-bold text-gray-900 mt-1">{recurrente.contratosVigentes}</p>
              </div>
              <div className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3">
                <p className="text-xs font-bold text-gray-500 uppercase">% con SEPA</p>
                <p className="text-lg font-bold text-gray-900 mt-1">{recurrente.pctSepa}%</p>
              </div>
              <div className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3">
                <p className="text-xs font-bold text-gray-500 uppercase">Altas / bajas este mes</p>
                <p className="text-lg font-bold text-gray-900 mt-1">{recurrente.altas} / {recurrente.bajas}</p>
              </div>
              <div className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3">
                <p className="text-xs font-bold text-gray-500 uppercase">% del total facturado</p>
                <p className="text-lg font-bold text-gray-900 mt-1">{recurrente.pctRecurrenteSobreTotal}%</p>
              </div>
            </div>
          </div>

          {/* Sección 9 — Calidad del dato */}
          <div className="bg-white rounded-2xl border shadow-sm p-5">
            <h3 className="font-bold text-gray-900 mb-3 text-sm">Calidad del dato — clasificación por línea de negocio</h3>
            <p className="text-[11px] text-gray-400 mb-3">
              Desde el mes en curso: {calidadDato.mesActual ? `${calidadDato.mesActual.sinLineas} de ${calidadDato.mesActual.totalFacturas} facturas (${calidadDato.mesActual.sinLineasPct}%) sin líneas — cayeron en SAT por descarte, sin poder comprobar instalación/mantenimiento.` : 'sin calcular todavía.'}
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b bg-gray-50/60">
                    <th className="text-left px-4 py-2 font-bold text-gray-500 uppercase">Línea</th>
                    <th className="text-right px-3 py-2 font-bold text-gray-500 uppercase">{anioAnterior} (nº)</th>
                    <th className="text-right px-3 py-2 font-bold text-gray-500 uppercase">{anioAnterior} (neto)</th>
                    <th className="text-right px-3 py-2 font-bold text-gray-500 uppercase">{anio} (nº)</th>
                    <th className="text-right px-3 py-2 font-bold text-gray-500 uppercase">{anio} (neto)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {LINEAS_ORDEN.map(l => {
                    const prev = calidadDato.porAnio[anioAnterior]?.[l] || { count: 0, neta: 0 };
                    const cur  = calidadDato.porAnio[anio]?.[l] || { count: 0, neta: 0 };
                    return (
                      <tr key={l}>
                        <td className="px-4 py-2 font-semibold text-gray-700">{LINEA_LABEL[l]}</td>
                        <td className="px-3 py-2 text-right">{prev.count}</td>
                        <td className="px-3 py-2 text-right">{fmtEur(prev.neta)}</td>
                        <td className="px-3 py-2 text-right">{cur.count}</td>
                        <td className="px-3 py-2 text-right">{fmtEur(cur.neta)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <ValidacionClasificacionPanel />
        </>
      )}
    </div>
  );
}
